import { useEffect, useRef } from 'react';
import { useAuthStore } from './authStore';
import { authApi, AuthApiError } from './authApi';
import { useUIStore, type ScreenName } from '@/stores/uiStore';
import { useUserStore } from '@/stores/userStore';
import { disconnectSocket } from '@/net/socket';
import { initPush } from '@/push/pushService';

// On a successful boot-time auth, we want to land the user somewhere useful
// — anywhere except the login screen. If they reloaded mid-match the match
// state is gone, so home is the safe landing.
const POST_LOGIN_SCREENS: ScreenName[] = ['home', 'leaderboard', 'settings'];

/**
 * On app boot: if we have a persisted token, fetch /me to verify it's still
 * valid and to refresh the profile. Drops the screen back to login on failure.
 * Also handles the OAuth callback URL fragment ("#auth=<jwt>&uid=...").
 */
export function useAuthBootstrap(): void {
  const ranRef = useRef(false);
  const setSession = useAuthStore((s) => s.setSession);
  const setStatus = useAuthStore((s) => s.setStatus);
  const setUser = useAuthStore((s) => s.setUser);
  const clear = useAuthStore((s) => s.clear);
  const token = useAuthStore((s) => s.token);
  const status = useAuthStore((s) => s.status);
  const go = useUIStore((s) => s.go);
  const setProfile = useUserStore((s) => s.setProfile);

  // Consume OAuth-callback fragment (#auth=<jwt>) on first render only.
  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    const hash = window.location.hash;
    if (!hash || hash.length < 2) return;
    const params = new URLSearchParams(hash.slice(1));
    const tok = params.get('auth');
    const err = params.get('auth_error');
    if (err) {
      console.warn('[auth] OAuth failed:', err);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      return;
    }
    if (!tok) return;
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    setStatus('loading');
    authApi
      .me(tok)
      .then(({ user }) => {
        setSession(tok, user);
        setProfile({
          id: user.id,
          name: user.displayName,
          avatarColor: user.avatarColor,
          avatarUrl: user.avatarUrl,
          mmr: user.mmr,
          mmrSolo: user.mmrSolo ?? user.mmr,
          mmrDuo: user.mmrDuo ?? user.mmr,
          rank: user.rank,
          rankDivision: user.rankDivision,
          rankStars: user.rankStars,
          rankDuo: user.rankDuo ?? user.rank,
          rankDivisionDuo: user.rankDivisionDuo ?? user.rankDivision,
          rankStarsDuo: user.rankStarsDuo ?? user.rankStars,
          coins: user.coins,
          gems: user.gems,
          xp: user.xp,
          winStreak: user.winStreak,
          streakSolo: user.streakSolo ?? 0,
          streakDuo: user.streakDuo ?? 0,
          matchesPlayed: user.matchesPlayed,
          matchesWon: user.matchesWon,
          frame: user.frame ?? null,
          characterIdleUrl: user.characterIdleUrl ?? null,
        });
        go('home');
      })
      .catch((e) => {
        console.warn('[auth] OAuth token check failed', e);
        clear();
      });
  }, [setSession, setStatus, clear, setProfile, go]);

  // Register for push as soon as we're authenticated — regardless of HOW we got
  // there (fresh login via LoginScreen, OAuth deep-link, or a persisted token).
  // initPush is idempotent, so a single effect here covers every path; this
  // fixes first-install logins where the token arrives via native deep-link
  // (not window.location.hash) and so never hit the bootstrap callbacks.
  useEffect(() => {
    if (status === 'authed' && token) void initPush();
  }, [status, token]);

  // Validate persisted token against /me on boot.
  useEffect(() => {
    if (status !== 'unknown') return;
    if (!token) {
      setStatus('anon');
      return;
    }
    setStatus('loading');
    authApi
      .me(token)
      .then(({ user }) => {
        setUser(user);
        setStatus('authed');
        setProfile({
          id: user.id,
          name: user.displayName,
          avatarColor: user.avatarColor,
          avatarUrl: user.avatarUrl,
          mmr: user.mmr,
          mmrSolo: user.mmrSolo ?? user.mmr,
          mmrDuo: user.mmrDuo ?? user.mmr,
          rank: user.rank,
          rankDivision: user.rankDivision,
          rankStars: user.rankStars,
          rankDuo: user.rankDuo ?? user.rank,
          rankDivisionDuo: user.rankDivisionDuo ?? user.rankDivision,
          rankStarsDuo: user.rankStarsDuo ?? user.rankStars,
          coins: user.coins,
          gems: user.gems,
          xp: user.xp,
          winStreak: user.winStreak,
          streakSolo: user.streakSolo ?? 0,
          streakDuo: user.streakDuo ?? 0,
          matchesPlayed: user.matchesPlayed,
          matchesWon: user.matchesWon,
          frame: user.frame ?? null,
          characterIdleUrl: user.characterIdleUrl ?? null,
        });
        // uiStore is not persisted, so on F5 the default screen is 'login'.
        // If we just verified an existing session, push the user past the
        // login screen so the F5 reload feels like a refresh, not a logout.
        const currentScreen = useUIStore.getState().screen;
        if (!POST_LOGIN_SCREENS.includes(currentScreen)) {
          go('home');
        }
      })
      .catch((e) => {
        if (e instanceof AuthApiError && (e.status === 401 || e.status === 404)) {
          clear();
          disconnectSocket();
          go('login');
        } else {
          // Network down — keep the cached session so the app still feels logged in.
          setStatus('authed');
          const currentScreen = useUIStore.getState().screen;
          if (!POST_LOGIN_SCREENS.includes(currentScreen)) {
            go('home');
          }
        }
      });
  }, [status, token, setUser, setStatus, setProfile, clear, go]);
}
